import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from './styles';
import { PreChat } from '../Prechat';
import { FeaturesReady, useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  CHARLIE_START_CHAT_MESSAGE, CHARLIE_ZENDESK_CHAT_TITLE,
  CHARLIE_ZENDESK_DELETE_SUNSHINE,
  CHARLIE_ZENDESK_SEND_MESSAGE_BUTTON_TITLE,
  CHAT_BOT,
  NEW_SUPPORT,
  ZENDESK_WIDGET_KEY,
  XRAY_ENABLE_CANCELL_WITH_SUPPORT,
  ZENDESK_CHAT_STATUS,
  ENABLE_SUPPORT_SERVICE_CONTROL,
  ATIVACAO_PREVENT_CONTINUE_TO_CHAT_ERROR,
  ATIVACAO_CONTEXT_N8N_TO_ZENDESK,
} from '@/config/GrowthBook/constants';
import {
  CHAT_TOKEN, ZENDESK_AUTH_KEY, ZENDESK_AUTH_TOKEN, COUNTRY,
  IS_PROD,
} from '@/config';
import jwt from 'jsonwebtoken';
import { useDispatch, useSelector } from 'react-redux';
import { isBRServer } from '@/utils/Validators/validation';
import logger from '@/utils/logger';
import useLocale from '@/hooks/useLocale';
import { commonActions, preChatActions } from '@/redux/modules';
import {
  addMinutes, differenceInMilliseconds, differenceInMinutes,
} from 'date-fns';
import { api } from '@/utils/api';
import { Chatbot } from '@/components/Chatbot';
import mountZendeskTags from '../Prechat/utils/mountZendeskTags';
import { getCache } from '@/utils/cacheControl';
import { CACHE_KEYS } from '@/enums/cache/cache.enum';

let sendStartChatMessageTimeout = null;
const ChatButton = () => {
  const [widgetIsOpen, setWidgetIsOpen] = useState(false);
  const { email, name, id } = useSelector(state => state.summary);
  const { token } = useSelector(state => state.auth);
  const {
    hasActiveChat, screenToShowInPreChat, showPreChat, n8nParams,
  } = useSelector(state => state.preChat);
  const { serverTime } = useSelector(state => state.common);
  const summaryState = useSelector(state => state.summary);
  const [zendeskExists, setZendeskExists] = useState(!!window.zE);
  const [isObservingChat, setIsObservingChat] = useState(false);
  const [dataToStartChat, setDataToStartChat] = useState({});
  const [shouldWaitDepartments, setShouldWaitDepartments] = useState(false);
  const { preChat: preChatLocale } = useLocale();
  const dispatch = useDispatch();

  const enableChatBot = useFeatureIsOn(CHAT_BOT);
  const chatTitle = useFeatureValue(CHARLIE_ZENDESK_CHAT_TITLE);
  const deleteSunshineUser = useFeatureIsOn(CHARLIE_ZENDESK_DELETE_SUNSHINE);
  const enableCancelWithSupport = useFeatureIsOn(XRAY_ENABLE_CANCELL_WITH_SUPPORT);
  const zendeskWidgetKey = useFeatureValue(ZENDESK_WIDGET_KEY);
  const startChatMessage = useFeatureValue(CHARLIE_START_CHAT_MESSAGE);
  const sendMessageButtonTitle = useFeatureValue(CHARLIE_ZENDESK_SEND_MESSAGE_BUTTON_TITLE);
  const useNewSupport = useFeatureIsOn(NEW_SUPPORT);
  const enableNewChatStatus = useFeatureIsOn(ZENDESK_CHAT_STATUS);
  const enableDepartmentVerification = useFeatureIsOn(ENABLE_SUPPORT_SERVICE_CONTROL);
  const usingContinueToChatFix = useFeatureIsOn(ATIVACAO_PREVENT_CONTINUE_TO_CHAT_ERROR);
  const zendeskWithN8nContext = useFeatureIsOn(ATIVACAO_CONTEXT_N8N_TO_ZENDESK);
  const intervalIds = useMemo(() => [], []);

  const downloadZendeskWebWidget = useCallback(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskWidgetKey || CHAT_TOKEN}`;
    document.body.appendChild(script);
  }, [zendeskWidgetKey]);

  const verifyHasRecentChatFinished = useCallback(() => {
    // control if a chat was finished and reset triggered in the last minute
    const resetHasBeenTriggered = localStorage.getItem('chatRecentFinished');
    const timeSinceReset = differenceInMilliseconds(new Date(), new Date(resetHasBeenTriggered));
    const oneMinute = 60000;
    if (resetHasBeenTriggered && timeSinceReset < oneMinute) {
      const timeToEnableReset = 60000 - timeSinceReset;
      setTimeout(() => { localStorage.removeItem('chatRecentFinished'); }, timeToEnableReset);
    } else {
      localStorage.removeItem('chatRecentFinished');
    }
  }, []);

  useEffect(() => {
    downloadZendeskWebWidget();
    verifyHasRecentChatFinished();
  }, [downloadZendeskWebWidget, verifyHasRecentChatFinished]);

  const verifyIfZendeskExists = useCallback(() => {
    if (!window.zE) {
      window.setTimeout(verifyIfZendeskExists, 1000);
      return;
    }

    setZendeskExists(true);

    // eslint-disable-next-line no-undef
    zE('messenger:on', 'open', () => {
      // eslint-disable-next-line
      const timeout = setTimeout(triggerChatFinish, 500);
      return () => clearTimeout(timeout);
    });

    // eslint-disable-next-line no-undef
    zE('messenger:on', 'close', () => {
      setWidgetIsOpen(false);
    });

    // eslint-disable-next-line no-undef
    zE('messenger:on', 'unreadMessages', (count) => {
      if (count >= 1) {
        // eslint-disable-next-line no-undef
        zE('messenger', 'open');
        setWidgetIsOpen(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    verifyIfZendeskExists();
  }, [verifyIfZendeskExists]);

  // force a start message on zendesk widget
  const clickOnSendMessageButton = useCallback((chatComponentDocument, inputElement) => {
    const sendBtnElement = chatComponentDocument && chatComponentDocument.querySelector(`[title="${sendMessageButtonTitle}"]`);

    if (!sendBtnElement) {
      inputElement.dispatchEvent(new Event('change', { bubbles: true }));
      window.setTimeout(clickOnSendMessageButton(chatComponentDocument, inputElement), 50);
      return;
    }

    enableNewChatStatus && dispatch(preChatActions.chat.start());

    sendBtnElement.click();
  }, [sendMessageButtonTitle, dispatch, enableNewChatStatus]);

  const sendStartChatMessage = useCallback(() => {
    const resetHasBeenTriggered = localStorage.getItem('resetHasBeenTriggered');
    const timeSinceReset = differenceInMinutes(new Date(), new Date(resetHasBeenTriggered));

    const recentChatFinished = resetHasBeenTriggered && timeSinceReset < 5;

    const n8nMessage = zendeskWithN8nContext && n8nParams && n8nParams.context && `Maia: ${n8nParams.context}`;

    if (widgetIsOpen && startChatMessage && !hasActiveChat && !recentChatFinished) {
      const chatComponent = document.querySelector(`[title="${chatTitle}"]`);
      const chatComponentDocument = chatComponent && chatComponent.contentWindow && chatComponent.contentWindow.document;
      const inputElement = chatComponentDocument && chatComponentDocument.getElementsByTagName('textarea')[0];

      if (!inputElement) {
        if (!usingContinueToChatFix) {
          window.setTimeout(sendStartChatMessage, 50);
        }

        if (usingContinueToChatFix) {
          clearTimeout(sendStartChatMessageTimeout);
          sendStartChatMessageTimeout = setTimeout(() => {
            sendStartChatMessage();
          }, 50);
        }
        return;
      }

      inputElement.innerText = n8nMessage || startChatMessage;
      inputElement.dispatchEvent(new Event('change', { bubbles: true }));

      if (inputElement.value === n8nMessage || startChatMessage) {
        clickOnSendMessageButton(chatComponentDocument, inputElement);
      }

      dispatch(preChatActions.chat.setActiveChat(true));

      const spans = chatComponentDocument && chatComponentDocument.querySelectorAll('span');
      spans && spans.forEach((spanElement) => {
        if (spanElement.textContent === (n8nMessage || startChatMessage) && spanElement.parentElement && spanElement.parentElement.parentElement) {
          // eslint-disable-next-line no-param-reassign
          spanElement.parentElement.parentElement.style.display = 'none';
        }
      });


      dispatch(preChatActions.n8n.forceOpenSupport(({ bool: false, n8nParams: {} })));
    }
  }, [
    widgetIsOpen,
    chatTitle,
    startChatMessage,
    clickOnSendMessageButton,
    hasActiveChat,
    dispatch,
    n8nParams,
  ]);

  useEffect(() => {
    sendStartChatMessage();
  }, [sendStartChatMessage]);

  const clearAllIntervals = useCallback(() => {
    while (intervalIds.length) {
      const id = intervalIds.pop();
      clearInterval(id);
    }
  }, [intervalIds]);

  const generateZendeskToken = useCallback(async () => {
    if (!email || !id || !name || id === 'undefined' || id === undefined) return;

    const zendeskToken = localStorage.getItem('HGZendeskTokenToChat');
    const zendeskCustomerId = `${isBRServer ? 'br_' : 'es_'}${String(id)}`;

    if (zendeskCustomerId.includes('undefined')) {
      logger.error('Zendesk - User undefined', { email, userId: id, name });
      return;
    }

    let token;

    if (zendeskToken) {
      jwt.verify(zendeskToken, ZENDESK_AUTH_TOKEN, (err) => {
        if (!err) {
          token = zendeskToken;
        }
      });

      // check if the token is from the current user
      const tokenData = jwt.decode(zendeskToken, { complete: true });
      if (!tokenData
        || tokenData.payload.external_id !== zendeskCustomerId
        || tokenData.payload.external_id === 'undefined'
        || tokenData.payload.external_id === undefined
        || tokenData.payload.external_id === 'br_undefined'
        || tokenData.payload.external_id === 'es_undefined'
      ) {
        token = null;
      }
    }

    if (token) return token;

    token = jwt.sign(
      {
        external_id: zendeskCustomerId,
        name,
        email,
        email_verified: true,
        scope: 'user',
      },
      ZENDESK_AUTH_TOKEN,
      {
        keyid: ZENDESK_AUTH_KEY,
        algorithm: 'HS256',
        expiresIn: '1w',
      },
    );

    localStorage.setItem('HGZendeskTokenToChat', token);

    return token;
  }, [email, id, name]);

  const loginZendeskUserAgain = useCallback(async (customCallback) => {
    const zendeskJWTToken = await generateZendeskToken();
    setIsObservingChat(false);

    // eslint-disable-next-line no-undef
    zE('messenger', 'loginUser', (zendeskCallback) => {
      zendeskCallback(zendeskJWTToken);
      customCallback && customCallback();
    });
  }, [generateZendeskToken]);


  const minimizeWidgetAndResetPrechat = useCallback(() => {
    // eslint-disable-next-line no-undef
    zE('messenger', 'close');
    // eslint-disable-next-line no-undef
    zE('messenger', 'logoutUser');
    setTimeout(loginZendeskUserAgain, 3000);

    localStorage.removeItem('resetHasBeenTriggered');
    localStorage.setItem('chatRestarted', true);
    dispatch(preChatActions.chat.setActiveChat(false));
    setIsObservingChat(false);
    clearAllIntervals();
  }, [dispatch, clearAllIntervals, loginZendeskUserAgain]);

  const verifyUserHasActiveChat = useCallback(() => {
    const hasConversationStarted = Object.keys(localStorage).filter(key => key.includes('.conversationStartedAt'))[0];
    if (!enableNewChatStatus && hasConversationStarted) {
      dispatch(preChatActions.chat.setActiveChat(true));
      setWidgetIsOpen(true);
      // eslint-disable-next-line no-undef
      zendeskExists && zE('messenger', 'open');
    } else {
      setTimeout(verifyUserHasActiveChat, 10000);
    }
  }, [dispatch, zendeskExists, enableNewChatStatus]);

  const verifyIfShouldOpenWidgetOnStart = useCallback(() => {
    const someDepartmentIsOpen = serverTime && serverTime.departments && serverTime.departments.some(department => department.status === 'open');

    if (enableNewChatStatus && hasActiveChat && someDepartmentIsOpen) {
      setWidgetIsOpen(true);
      // eslint-disable-next-line no-undef
      zendeskExists && zE('messenger', 'open');
    }

    if ((enableNewChatStatus && hasActiveChat !== undefined && !hasActiveChat) || (enableNewChatStatus && !someDepartmentIsOpen)) {
      setWidgetIsOpen(false);
      // eslint-disable-next-line no-undef
      zendeskExists && zE('messenger', 'close');
    }
  }, [hasActiveChat, enableNewChatStatus, zendeskExists, serverTime]);

  useEffect(() => {
    verifyIfShouldOpenWidgetOnStart();
  }, [verifyIfShouldOpenWidgetOnStart]);

  const watchForChatRestart = useCallback(() => {
    if (!enableNewChatStatus) {
      dispatch(preChatActions.chat.setActiveChat(false));
      verifyUserHasActiveChat();
      clearAllIntervals();
      setIsObservingChat(false);
    }
  }, [clearAllIntervals, dispatch, verifyUserHasActiveChat, enableNewChatStatus]);

  useEffect(() => {
    const chatRestarted = localStorage.getItem('chatRestarted');
    if (!widgetIsOpen && chatRestarted && zendeskExists) {
      watchForChatRestart();

      if (name && email && id) {
        setTimeout(loginZendeskUserAgain, 3000);
      }
    }
  }, [widgetIsOpen, watchForChatRestart, zendeskExists, loginZendeskUserAgain, email, id, name]);

  const hideChatFinishAlert = useCallback(() => {
    localStorage.removeItem('resetHasBeenTriggered');

    const chatComponent = document.querySelector(`[title="${chatTitle}"]`);
    const chatComponentDocument = chatComponent && chatComponent.contentWindow && chatComponent.contentWindow.document;
    const inputComponent = chatComponentDocument && chatComponentDocument.getElementsByTagName('textarea')[0];
    const chatBottom = inputComponent && inputComponent.parentElement && inputComponent.parentElement.parentElement && inputComponent.parentElement.parentElement.parentElement;
    if (chatBottom && chatBottom.childNodes) {
      chatBottom.childNodes.forEach((child) => {
        // eslint-disable-next-line no-param-reassign
        child.style.display = 'none';
      });

      chatBottom.childNodes[0].style.display = 'block';
      chatBottom.childNodes[2].style.display = 'block';
    }
  }, [chatTitle]);

  const triggerChatFinish = useCallback(() => {
    const chatComponent = document.querySelector(`[title="${chatTitle}"]`);
    const chatComponentDocument = chatComponent && chatComponent.contentWindow && chatComponent.contentWindow.document;
    const chatLog = chatComponentDocument && chatComponentDocument.querySelector('[role="log"]');
    const hasResetMessage = chatLog && chatLog.children && chatLog.children.length
      && [...chatLog.children].some(child => preChatLocale.resetMessages && preChatLocale.resetMessages.some(resetMessage => child.innerText.includes(resetMessage)) && child.innerHTML.includes(isBRServer ? 'Agora mesmo' : 'Hace un momento'));

    const resetHasBeenTriggered = localStorage.getItem('resetHasBeenTriggered');
    const timeSinceReset = differenceInMinutes(new Date(), new Date(resetHasBeenTriggered));

    const chatRecentFinished = localStorage.getItem('chatRecentFinished');
    const timeSinceLastChatFinished = differenceInMinutes(new Date(), new Date(chatRecentFinished));

    // recent chat finish || chat finished in the last 5 minutes
    if ((hasResetMessage && !chatRecentFinished && timeSinceLastChatFinished > 1) || (resetHasBeenTriggered && timeSinceReset < 5)) {
      // here we are going to hide and disable the input and show a message to the user that the chat has been finished
      const inputComponent = chatComponentDocument && chatComponentDocument.getElementsByTagName('textarea')[0];
      const chatBottom = inputComponent && inputComponent.parentElement && inputComponent.parentElement.parentElement && inputComponent.parentElement.parentElement.parentElement;
      if (chatBottom) {
        chatBottom.childNodes.forEach((child) => {
          // eslint-disable-next-line no-param-reassign
          child.style.display = 'none';
        });

        inputComponent.disabled = true;

        const alertWrapper = chatComponentDocument.createElement('div');
        alertWrapper.style.display = 'flex';
        alertWrapper.style.flexDirection = 'column';
        alertWrapper.style.fontFamily = 'system-ui';
        alertWrapper.style.justifyContent = 'center';
        alertWrapper.style.alignItems = 'center';
        alertWrapper.style.padding = '0px 16px 16px 16px';
        alertWrapper.style.bottom = '0px';
        alertWrapper.style.left = '0px';
        alertWrapper.style.width = '100%';
        alertWrapper.innerHTML = `
          <p style="color: #707070; text-align: center; font-size: 14px; font-weight: 400; max-width: 325px; margin-bottom: 16px;">
            ${preChatLocale.chatFinished}
          </p>
          `;

        chatBottom.appendChild(alertWrapper);

        const startNewChatButton = chatComponentDocument.createElement('button');
        startNewChatButton.style.height = '40px';
        startNewChatButton.style.padding = '0px 24px';
        startNewChatButton.style.justifyContent = 'center';
        startNewChatButton.style.alignItems = 'center';
        startNewChatButton.style.flexShrink = '0';
        startNewChatButton.style.borderRadius = '24px';
        startNewChatButton.style.background = '#0070D1';
        startNewChatButton.style.border = 'none';
        startNewChatButton.style.color = '#FFF';
        startNewChatButton.style.textAlign = 'center';
        startNewChatButton.style.fontFamily = 'system-ui';
        startNewChatButton.style.fontSize = '16px';
        startNewChatButton.style.fontStyle = 'normal';
        startNewChatButton.style.fontWeight = '500';
        startNewChatButton.style.lineHeight = 'normal';
        startNewChatButton.style.cursor = 'pointer';
        startNewChatButton.innerText = preChatLocale.startNewChat;

        alertWrapper.appendChild(startNewChatButton);

        startNewChatButton.addEventListener('mouseover', () => {
          startNewChatButton.style.backgroundColor = '#2E93EE';
        });

        startNewChatButton.addEventListener('mouseout', () => {
          startNewChatButton.style.backgroundColor = '#0070D1';
        });

        startNewChatButton.addEventListener('click', async () => {
          await hideChatFinishAlert();
          await minimizeWidgetAndResetPrechat();
          dispatch(preChatActions.preChat.setShowPreChat(true));
        });

        enableNewChatStatus && dispatch(preChatActions.chat.finish());
      }

      const hasConversationStarted = Object.keys(localStorage).filter(key => key.includes('.conversationStartedAt'))[0];
      localStorage.removeItem(hasConversationStarted);

      if (!intervalIds.length && timeSinceReset < 10) {
        const timeToDispatch = addMinutes(new Date(resetHasBeenTriggered), 10);
        const remainingTime = differenceInMilliseconds(timeToDispatch, new Date());
        const timeoutId = window.setTimeout(minimizeWidgetAndResetPrechat, remainingTime);
        intervalIds.push(timeoutId);
      }

      if (!resetHasBeenTriggered) {
        localStorage.setItem('resetHasBeenTriggered', new Date().toISOString());
        // will be removed after 1 minute, even if the user didn't start a new chat, is to prevent the reset again in case the user starts a new chat because of the "agora mesmo" + resetMessage
        localStorage.setItem('chatRecentFinished', new Date().toISOString());
        setTimeout(() => { localStorage.removeItem('chatRecentFinished'); }, 60000);
      }
    } else {
      // hide the chat finished message and enable the input
      hideChatFinishAlert();
      const inputComponent = chatComponentDocument && chatComponentDocument.getElementsByTagName('textarea')[0];
      if (inputComponent) {
        inputComponent.disabled = false;
      }
    }
  }, [chatTitle, minimizeWidgetAndResetPrechat, preChatLocale, intervalIds, hideChatFinishAlert, dispatch, enableNewChatStatus]);

  const observeZendeskChat = useCallback(() => {
    const chatComponent = document.querySelector(`[title="${chatTitle}"]`);
    const chatComponentDocument = chatComponent && chatComponent.contentWindow && chatComponent.contentWindow.document;
    const inputComponent = chatComponentDocument && chatComponentDocument.getElementsByTagName('textarea')[0];
    const chatLog = chatComponentDocument && chatComponentDocument.querySelector('[role="log"]');

    if (!inputComponent && !chatLog) {
      window.setTimeout(observeZendeskChat, 500);
      return;
    }

    setIsObservingChat(true);
    const config = { attributes: true, childList: true, subtree: true };

    const logObserver = new MutationObserver(triggerChatFinish);
    logObserver.observe(chatLog, config);
  }, [chatTitle, triggerChatFinish]);

  const verifyZendeskLoginRetry = useCallback((retry) => {
    const loggedInZendesk = !isBRServer || Object.keys(localStorage).filter(key => key.includes('.appUserId'))[0];
    if (loggedInZendesk) {
      verifyUserHasActiveChat();
    } else if (retry < 2) {
      loginZendeskUserAgain(setTimeout(() => verifyZendeskLoginRetry(retry + 1), 10000));
    } else {
      const zendeskCustomerId = `${isBRServer ? 'br_' : 'es_'}${String(id)}`;
      const zendeskJWTToken = generateZendeskToken();
      logger.info('Zendesk - User is not logged in zendesk', {
        email, zendeskCustomerId, name, zendeskJWTToken,
      });
    }
  }, [id, email, name, verifyUserHasActiveChat, generateZendeskToken, loginZendeskUserAgain]);

  const clearUserOnSunshine = useCallback(async (retry) => {
    const requestClearSunshineUser = {
      headers: {
        Authorization: token,
        email,
        brand: COUNTRY.toLowerCase(),
      },
      url: 'v2/clear/zendesk',
      data: {
        external_id: String(id),
      },
    };

    try {
      await api.customerSupport.post(requestClearSunshineUser);
      logger.info('Zendesk - Sunshine user cleared', { email, id });

      setTimeout(() => {
        const zendeskJWTToken = generateZendeskToken();

        // eslint-disable-next-line no-undef
        zE('messenger', 'loginUser', (callback) => {
          callback(zendeskJWTToken);
          setTimeout(() => verifyZendeskLoginRetry(0), 10000);
        });
      }, 10000);
    } catch (e) {
      if (e.response && e.response.status !== 404) {
        logger.error('Zendesk - Error clearing sunshine user', e);
        if (retry < 2) {
          clearUserOnSunshine(retry + 1);
          return;
        }
      }

      setTimeout(() => {
        const zendeskJWTToken = generateZendeskToken();

        // eslint-disable-next-line no-undef
        zE('messenger', 'loginUser', (callback) => {
          callback(zendeskJWTToken);
          setTimeout(() => verifyZendeskLoginRetry(0), 10000);
        });
      }, 10000);
    }
  }, [id, token, generateZendeskToken, email, verifyZendeskLoginRetry]);

  const verifyZendeskLogin = useCallback(() => {
    const loggedInZendesk = !isBRServer || Object.keys(localStorage).filter(key => key.includes('.appUserId'))[0];
    if (loggedInZendesk) {
      verifyUserHasActiveChat();
    } else {
      deleteSunshineUser && clearUserOnSunshine(0);
    }
  }, [clearUserOnSunshine, deleteSunshineUser, verifyUserHasActiveChat]);

  const loginZendeskUser = useCallback(async () => {
    const hasConversationStarted = Object.keys(localStorage).filter(key => key.includes('.conversationStartedAt'))[0];
    localStorage.removeItem(hasConversationStarted);

    const zendeskJWTToken = await generateZendeskToken();

    // eslint-disable-next-line no-undef
    zE('messenger', 'loginUser', (callback) => {
      callback(zendeskJWTToken);
      setTimeout(verifyZendeskLogin, 10000);
    });
  }, [verifyZendeskLogin, generateZendeskToken]);

  const verifyChatStatus = useCallback(() => {
    const someDepartmentIsOpen = enableDepartmentVerification ? serverTime && serverTime.departments && serverTime.departments.some(department => department.status === 'open') : true;

    someDepartmentIsOpen && enableNewChatStatus && dispatch(preChatActions.chat.status());
  }, [dispatch, enableNewChatStatus, serverTime, enableDepartmentVerification]);

  useEffect(() => {
    if (name && email && id && zendeskExists) {
      // eslint-disable-next-line no-undef
      zE('messenger:set', 'locale', `${isBRServer ? 'pt-br' : 'es'}`);
      loginZendeskUser();

      verifyChatStatus();
    }
  }, [zendeskExists, loginZendeskUser, email, id, name, verifyChatStatus]);

  useEffect(() => {
    if (!isObservingChat && widgetIsOpen) {
      observeZendeskChat();
    }
  }, [widgetIsOpen, observeZendeskChat, isObservingChat]);

  const verifyChatWithSupport = useCallback(() => {
    if (enableCancelWithSupport && hasActiveChat && !widgetIsOpen) {
      verifyUserHasActiveChat();
    }
  }, [hasActiveChat, widgetIsOpen, enableCancelWithSupport, verifyUserHasActiveChat]);

  useEffect(() => {
    verifyChatWithSupport();
  }, [verifyChatWithSupport]);

  const handleStartZenDeskChat = useCallback(async ({
    department,
    primaryReason,
    secondaryReason,
    product,
    hostingId,
    domain,
    isTicket,
    priority,
    chosenDepartment,
  }) => {
    if (enableChatBot && screenToShowInPreChat !== 'chatbot') {
      setTimeout(() => dispatch(preChatActions.preChat.setScreen('chatbot'), 600));
      const dataToZendesk = {
        department,
        primaryReason,
        secondaryReason,
        product,
        hostingId,
        domain,
        isTicket,
        priority,
        chosenDepartment,
      };

      setDataToStartChat(dataToZendesk);
      return;
    }

    try {
      const { id } = summaryState;
      if (enableDepartmentVerification) {
        const cacheKey = CACHE_KEYS.SUPPORT_DEPARTMENT_STATUS;
        const cachedDepartments = getCache(cacheKey);

        if (!cachedDepartments) {
          const dataToZendesk = {
            primaryReason,
          };

          dispatch(commonActions.serverTime.set(null));
          setDataToStartChat(dataToZendesk);
          dispatch(commonActions.serverTime.request());
          setShouldWaitDepartments(true);
          return;
        }

        const selectedDepartment = serverTime && serverTime.departments && serverTime.departments.find(dep => dep.slug === chosenDepartment);

        if (!selectedDepartment || selectedDepartment.status !== 'open') {
          // need those 50ms so when pre-chat open don't open on diagnostic screen
          setTimeout(() => dispatch(preChatActions.preChat.setScreen('triage')), 50);
          return;
        }
      }

      const tags = await mountZendeskTags({
        department,
        primaryReason,
        secondaryReason,
        product,
        whmcsId: id,
        departmentLocales: preChatLocale.zopimDepartments,
        priority,
        isTicket,
        v2: useNewSupport,
      });

      if (zendeskWithN8nContext && n8nParams) {
        const isArray = typeof n8nParams.category === 'object' && Array.isArray(n8nParams.category);
        if (!isArray) {
          tags.push(n8nParams.category);
        }

        if (isArray) {
          n8nParams.category.forEach((tag) => {
            tags.push(tag);
          });
        }
      }

      // eslint-disable-next-line no-undef
      zE('messenger:set', 'conversationFields', [
        { id: IS_PROD ? '27835972869267' : '26102773393171', value: domain },
        { id: IS_PROD ? '28285611660947' : '26102798680595', value: String(hostingId) },
      ]);

      if (!tags || (tags && !tags.length)) {
        logger.info('chat sem tag', {
          department, primaryReason, id,
        });
      }
      // eslint-disable-next-line no-undef
      zE('messenger:set', 'conversationTags', tags);
      await new Promise(resolve => setTimeout(resolve, 500));

      localStorage.removeItem('chatRestarted');
      setWidgetIsOpen(true);

      // eslint-disable-next-line no-undef
      zE('messenger', 'open');

      dispatch(preChatActions.preChat.setShowPreChat(false));
    } catch (e) {
      logger.error('Error on PreChat - method handleStartZenDeskChat', e);
    }
  }, [dispatch, summaryState, preChatLocale, serverTime, useNewSupport, enableChatBot, screenToShowInPreChat, enableDepartmentVerification]);

  const startChatAfterGetChatDepartments = useCallback(() => {
    if (enableDepartmentVerification && shouldWaitDepartments && serverTime && serverTime.departments) {
      handleStartZenDeskChat(dataToStartChat);
      setShouldWaitDepartments(false);
      !showPreChat && dispatch(preChatActions.preChat.setShowPreChat(true));
    }
  }, [enableDepartmentVerification, handleStartZenDeskChat, dataToStartChat, shouldWaitDepartments, serverTime, dispatch, showPreChat]);

  useEffect(() => {
    startChatAfterGetChatDepartments();
  }, [startChatAfterGetChatDepartments]);

  return (
    <FeaturesReady timeout={5000}>
      { zendeskExists && (
        <PreChat
          hasActiveChat={hasActiveChat}
          setWidgetIsOpen={setWidgetIsOpen}
          widgetIsOpen={widgetIsOpen}
          enableChatBot={enableChatBot}
          handleStartZenDeskChat={handleStartZenDeskChat}
        />
      )}

      {enableChatBot && screenToShowInPreChat === 'chatbot' && (
        <Chatbot
          handleStartZenDeskChat={handleStartZenDeskChat}
          dataToStartChat={dataToStartChat}
        />
      )}
    </FeaturesReady>
  );
};

export default withStyles(styles)(ChatButton);
